
import Component from 'vue-class-component';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import {PermissionsGroup} from '@/store/models.def';
import echarts from 'echarts';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, sqlExtractYear, sqlExtractMonth, sqlSum, sqlSumByDocType, getTableName } from '@/worker/db/sqlHelpers';
import moment from 'moment/moment';
import {GobiColor} from '@/helpers/color';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
  components: {
    IncludeDebitNoteToggle,
    IncludeCreditNoteToggle,
    SlPhModuleOnlyToggle,
    SecondaryLineBarChartFilterable,
  },
})
export default class SalesTotalSalesComparisonByPeriodBarChart extends FilterWidget {
  public get permissionIds(): PermissionsGroup[] {
    return ['sales', 'customers'];
  }

  public dataZoom = [
    {
      type: 'slider',
    },
    {
      type: 'inside',
    },
  ];

  public filterIds: Array<
      | 'dateAsOf'
      | 'dateRange'
      | 'stockItems'
      | 'agents'
      | 'customers'
      | 'suppliers'
      | 'compareDate1'
      | 'compareDate2'
      | 'comparisonPeriod'
  > = [
    'customers',
    'agents',
    'compareDate1',
    'compareDate2',
    'comparisonPeriod',
  ];

  public xAxisData: string[] = [];
  public series: echarts.ECharts[] | any = [];

  public get currencySymbol() {
    return gdbx.currencySymbol;
  }

  public get yAxisLabel2() {
    let label: string = '';

    label = this.currencySymbol;

    return label;
  }

  public get dataViewFormatter() {
    return DataViewFormatter.salesTracker;
  }

  public get includeCreditNote() {
    return settingx.now.includeCreditNote;
  }

  public get includeDebitNote() {
    return settingx.now.includeDebitNote;
  }

  public get slPhModuleOnly() {
    return settingx.now.slPhModuleOnly;
  }

  public get dataDateRange() {
    return [
      moment(this.selectedDateRange[0]).subtract(1, 'year').valueOf(),
      this.selectedDateRange[1],
    ];
  }

  public get expensiveHook() {
    const {
        selectedDateRange,
        includeDebitNote,
        includeCreditNote,
        slPhModuleOnly,
    } = this;
    return JSON.stringify([
        selectedDateRange,
        includeDebitNote,
        includeCreditNote,
        slPhModuleOnly,
    ]);
  }

  public async expensiveCalc() {
    const jobId = this.currentJobId;
    this.generateLoadingText(0.2, 0.4);

    const queryHead = await this._ensureTablesLoaded(
      ['sales', 'creditNotes', 'customerTrades'] as any[],
    );
    this.checkInterrupted(jobId);

    const salesTable = getTableName('sales');
    const cnTable = getTableName('creditNotes');
    const arTable = getTableName('customerTrades');

    const pastYearDateRange: [number, number] = [
      moment(this.selectedDateRange[0]).subtract(1, 'year').valueOf(),
      moment(this.selectedDateRange[1]).subtract(1, 'year').valueOf(),
    ];

    // Build month labels from this year's date range
    const monthLabels: string[] = [];
    const monthKeys: string[] = [];
    const mmt = moment(this.selectedDateRange[0]);
    const endMmt = moment(this.selectedDateRange[1]);
    while (mmt.isSameOrBefore(endMmt, 'month')) {
      monthLabels.push(mmt.format('MMM YY'));
      monthKeys.push(`${mmt.year()}-${mmt.month() + 1}`);
      mmt.add(1, 'month');
    }

    // Past year month keys (same offset)
    const pastMonthKeys: string[] = [];
    const pmmt = moment(pastYearDateRange[0]);
    const pendMmt = moment(pastYearDateRange[1]);
    while (pmmt.isSameOrBefore(pendMmt, 'month')) {
      pastMonthKeys.push(`${pmmt.year()}-${pmmt.month() + 1}`);
      pmmt.add(1, 'month');
    }

    const tyDateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
    const pyDateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${pastYearDateRange[0]} AND ${pastYearDateRange[1]}`;

    let thisYearData: number[] = [];
    let pastYearData: number[] = [];

    if (this.slPhModuleOnly) {
      // ========================================
      // SL Mode - use sales + creditNotes tables
      // ========================================

      const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
      const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

      const slMonthQueryForTable = (table: string, filter: string) => `
        SELECT ${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)},
          ${sqlSum(COL.AMOUNT, 'totalAmt')}
        FROM ${table}
        WHERE ${filter}
        GROUP BY year, month
        ORDER BY year, month
      `;

      this.generateLoadingText(0.4, 0.9);

      const [tySales, pySales, tyCN, pyCN] = await Promise.all([
        queryHead.rawQuery(slMonthQueryForTable(salesTable, `${tyDateFilter}${docTypeFilter}`)),
        queryHead.rawQuery(slMonthQueryForTable(salesTable, `${pyDateFilter}${docTypeFilter}`)),
        queryHead.rawQuery(slMonthQueryForTable(cnTable, tyDateFilter)),
        queryHead.rawQuery(slMonthQueryForTable(cnTable, pyDateFilter)),
      ]);
      this.checkInterrupted(jobId);

      const toMap = (rows: any[]) => {
        const m = new Map<string, number>();
        for (const r of rows) { m.set(`${r.year}-${r.month}`, Number(r.totalAmt) || 0); }
        return m;
      };
      const tySalesMap = toMap(tySales);
      const pySalesMap = toMap(pySales);
      const tyCNMap = toMap(tyCN);
      const pyCNMap = toMap(pyCN);

      thisYearData = monthKeys.map((key) => {
        const sales = tySalesMap.get(key) || 0;
        const cn = this.includeCreditNote ? (tyCNMap.get(key) || 0) : 0;
        return sales - cn;
      });

      pastYearData = pastMonthKeys.map((key) => {
        const sales = pySalesMap.get(key) || 0;
        const cn = this.includeCreditNote ? (pyCNMap.get(key) || 0) : 0;
        return sales - cn;
      });
    } else {
      // ========================================
      // AR Mode - use customerTrades table
      // ========================================

      const arMonthQueryForFilter = (filter: string) => `
        SELECT ${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)},
          ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
          ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
          ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')}
        FROM ${arTable}
        WHERE ${filter}
        GROUP BY year, month
        ORDER BY year, month
      `;

      this.generateLoadingText(0.4, 0.9);

      const [tyAR, pyAR] = await Promise.all([
        queryHead.rawQuery(arMonthQueryForFilter(tyDateFilter)),
        queryHead.rawQuery(arMonthQueryForFilter(pyDateFilter)),
      ]);
      this.checkInterrupted(jobId);

      const toArMap = (rows: any[]) => {
        const m = new Map<string, { iv: number; dn: number; cn: number }>();
        for (const r of rows) {
          m.set(`${r.year}-${r.month}`, {
            iv: Number(r.iv_amount) || 0,
            dn: Number(r.dn_amount) || 0,
            cn: Number(r.cn_amount) || 0,
          });
        }
        return m;
      };
      const tyMap = toArMap(tyAR);
      const pyMap = toArMap(pyAR);

      thisYearData = monthKeys.map((key) => {
        const d = tyMap.get(key);
        if (!d) { return 0; }
        return d.iv + (this.includeDebitNote ? d.dn : 0) - (this.includeCreditNote ? d.cn : 0);
      });

      pastYearData = pastMonthKeys.map((key) => {
        const d = pyMap.get(key);
        if (!d) { return 0; }
        return d.iv + (this.includeDebitNote ? d.dn : 0) - (this.includeCreditNote ? d.cn : 0);
      });
    }

    this.generateLoadingText(1);

    this.xAxisData = monthLabels;

    this.series = [
      {
        name: 'This Year',
        type: 'bar',
        smooth: true,
        barWidth: '20%',
        color: GobiColor.COLOR1,
        data: thisYearData,
      },
      {
        name: 'Last Year',
        type: 'bar',
        smooth: true,
        barWidth: '20%',
        color: GobiColor.COLOR3,
        data: pastYearData,
      },
    ];

    this.saveHistory('series', 'xAxisData');
  }
}
